<script setup>
/*
 * Nedtaelling.
 *
 * TO UDGAVER AF DEN SAMME SANDHED, og det er med vilje.
 *
 * Serveren regner dagene og skriver dem i teksten. En nedtaelling der kun
 * findes i JavaScript er tom for en soegemaskine, og "om 72 dage" er
 * netop dét en kampagneside skal kunne sige. Den saetning er ogsaa det
 * eneste der vises hvis JavaScript ikke koerer.
 *
 * Naar browseren er med, traeder uret i stedet: dage, timer, minutter og
 * sekunder der tikker. Det regnes mod `maalIso` og ikke mod et restantal
 * fra serveren — siden kan ligge i svar-cachen i op til en halv time, og
 * saa ville vi taelle ned fra et tal der var et kvarter gammelt.
 *
 * Skiftet sker foerst i onMounted. Ellers ville serverens HTML og
 * browserens foerste tegning vaere forskellige, og Vue ville klage over
 * hydrering.
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

const props = defineProps({ data: { type: Object, required: true } });

const klient = ref(false);
const naa = ref(Date.now());
let ur = null;

const maal = computed(() => (props.data.maalIso ? Date.parse(props.data.maalIso) : null));

const rest = computed(() => {
    if (!maal.value) return null;

    const sek = Math.max(0, Math.floor((maal.value - naa.value) / 1000));

    return {
        dage: Math.floor(sek / 86400),
        timer: Math.floor((sek % 86400) / 3600),
        minutter: Math.floor((sek % 3600) / 60),
        sekunder: sek % 60,
        slut: sek === 0,
    };
});

// Nul-udfyldt, saa cifrene ikke hopper i bredden når 9 bliver til 10.
function to(n) {
    return String(n).padStart(2, '0');
}

const felter = computed(() => {
    if (!rest.value) return [];

    return [
        { navn: rest.value.dage === 1 ? 'dag' : 'dage', vaerdi: String(rest.value.dage) },
        { navn: 'timer', vaerdi: to(rest.value.timer) },
        { navn: 'min.', vaerdi: to(rest.value.minutter) },
        { navn: 'sek.', vaerdi: to(rest.value.sekunder) },
    ];
});

onMounted(() => {
    klient.value = true;
    naa.value = Date.now();

    /*
     * Vi laeser uret hvert sekund i stedet for at traekke ét fra. En fane
     * der har ligget i baggrunden faar sine timere struben, og et taelleur
     * ville vaere bagud naar man kom tilbage.
     */
    ur = window.setInterval(() => { naa.value = Date.now(); }, 1000);
});

onBeforeUnmount(() => {
    if (ur !== null) window.clearInterval(ur);
});
</script>

<template>
    <p v-if="!data.dato" class="text-secondary small mb-0">Ingen dato valgt.</p>

    <div v-else class="nedtaelling">
        <p v-if="data.etiket" class="nedtaelling__etiket">{{ data.etiket }}</p>

        <!-- Serverens udgave: staar i HTML, ogsaa uden JavaScript. -->
        <template v-if="!klient || !rest">
            <p v-if="data.overstaaet" class="nedtaelling__linje">
                <strong>{{ data.datoLabel }}</strong> er passeret.
            </p>
            <p v-else-if="data.dage === 0" class="nedtaelling__linje">
                Det er <strong>i dag</strong> — {{ data.datoLabel }}.
            </p>
            <p v-else class="nedtaelling__linje">
                <strong class="nedtaelling__tal">{{ data.dage }}</strong>
                {{ data.dage === 1 ? 'dag' : 'dage' }} til <strong>{{ data.datoLabel }}</strong>
            </p>
        </template>

        <!-- Browserens udgave: uret. -->
        <template v-else>
            <p v-if="rest.slut" class="nedtaelling__linje">
                <strong>{{ data.datoLabel }}</strong> — det er nu.
            </p>

            <template v-else>
                <div class="ur" role="timer" :aria-label="`Der er ${rest.dage} dage, ${rest.timer} timer og ${rest.minutter} minutter til ${data.datoLabel}`">
                    <div v-for="f in felter" :key="f.navn" class="ur__felt">
                        <span class="ur__ciffer">
                            <Transition name="rul" mode="out-in">
                                <span :key="f.vaerdi">{{ f.vaerdi }}</span>
                            </Transition>
                        </span>
                        <span class="ur__navn">{{ f.navn }}</span>
                    </div>
                </div>
                <p class="nedtaelling__linje nedtaelling__maal">til {{ data.datoLabel }}</p>
            </template>
        </template>
    </div>
</template>

<style scoped>
/*
 * PAA GRADIENTEN SKAL TEKSTEN VAERE MOERK.
 *
 * Foerste udgave satte tallet i --pc-accent, som er NØJAGTIG samme
 * groenne som gradientens start — tallet forsvandt i sin egen baggrund.
 * --pc-on-accent findes til praecis det her og giver 10,4:1.
 */
.nedtaelling {
    position: relative;
    overflow: hidden;
    border-radius: var(--pc-radius-lg);
    padding: 1.75rem 2rem 1.9rem;
    background: var(--pc-accent);
    background-image: var(--pc-gradient, none);
    color: var(--pc-on-accent, #0A0C10);
    text-align: center;
}

.nedtaelling__etiket {
    color: inherit;
    opacity: .72;
    font-size: .78rem; letter-spacing: .14em; text-transform: uppercase;
    font-weight: 700;
    margin: 0 0 .75rem;
}

.nedtaelling__linje { color: inherit; font-size: 1.05rem; margin: 0; }
.nedtaelling__maal { margin-top: .9rem; opacity: .8; font-size: .95rem; }

.nedtaelling__tal {
    display: block;
    font-size: clamp(3.5rem, 13vw, 5.5rem);
    line-height: 1;
    font-weight: 800;
    color: inherit;
    font-variant-numeric: tabular-nums;
    margin-bottom: .15rem;
}

.ur {
    display: flex; justify-content: center; align-items: flex-start;
    gap: clamp(.4rem, 2.5vw, 1.1rem);
}

.ur__felt {
    display: flex; flex-direction: column; align-items: center; gap: .35rem;
    min-width: clamp(3.4rem, 15vw, 5.6rem);
    padding: .7rem .4rem .6rem;
    border-radius: var(--pc-radius);
    /*
     * En anelse moerkere end gradienten, ikke en farve for sig. Kortene
     * skal loefte cifrene fra baggrunden uden at blive en andet element.
     */
    background: rgb(0 0 0 / 8%);
}

.ur__ciffer {
    display: block;
    font-size: clamp(1.9rem, 8vw, 3.4rem);
    line-height: 1;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    letter-spacing: -.02em;
}

.ur__navn {
    font-size: .68rem; letter-spacing: .12em; text-transform: uppercase;
    font-weight: 700; opacity: .7;
}

/*
 * Cifret ruller op naar det skifter. Kort nok til at sekundet naar at
 * staa stille, langt nok til at oejet ser bevaegelsen.
 */
.rul-enter-active, .rul-leave-active { transition: transform .22s ease, opacity .22s ease; }
.rul-enter-from { transform: translateY(55%); opacity: 0; }
.rul-leave-to { transform: translateY(-55%); opacity: 0; }

/*
 * Nogen har slaaet bevaegelse fra i systemet, og en nedtaelling er ikke
 * vigtigere end det. Tallene skifter stadig, de glider bare ikke.
 */
@media (prefers-reduced-motion: reduce) {
    .rul-enter-active, .rul-leave-active { transition: none; }
}
</style>
